import { SharedField, useDeviceTypeCustomControlsEditor } from '@admin/hooks/useDeviceTypeCustomControlsEditor';
import styled from '@emotion/styled';
import { FormControlLabel, Switch, TextField } from '@mui/material';
import Button from '@shared/components/core/button/CoreButton';
import SplitButton from '@shared/components/core/button/SplitButton';
import TextButton from '@shared/components/core/button/TextButton';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import Icons from '@shared/components/core/images/Icons';
import ChipButtonContainer from '@shared/components/core/layouts/ChipButtonContainer';
import AccordionRow from '@shared/components/core/row/AccordionRow';
import { BodySmall, LightXSmall, TextSize80, TitleXSmall } from '@shared/components/core/text/Typography';
import CommandField from '@shared/components/input/field/CommandField';
import { SpacedProgress } from '@shared/components/loading/SpacedLoader';
import { DefaultVoiceLocale } from '@shared/constants/localization/LocalizationConstants';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useEditDeviceTypeStore } from '@shared/hooks/state/EditDeviceTypeContainer';
import useResolveCurrentOrg from '@shared/hooks/useResolveCurrentOrg';
import { CustomControlModel } from '@shared/models/devices/CustomControlModel';
import { VoiceLocales } from '@shared/models/localization/Locales';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const ContentWrapper = styled.div`
    display: flex;
    flex: 1;
    gap: 0.5rem;
`;

const Sidebar = styled.aside`
    display: flex;
    width: fit-content;
    flex-shrink: 0;
    flex-direction: column;
    padding-right: 0.5rem;
    border-right: 1px solid ${(props) => props.theme.color.border};
    background-color: var(--sidebar);
    gap: 1rem;
`;

const SectionLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding-top: 1rem;
    color: var(--muted-foreground);
    ${TextSize80}
`;

const NavList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const CompletionButton = styled.button<{ $isActive: boolean }>`
    ${TextSize80}
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.375rem 0.5rem;
    border-radius: 0.375rem;
    background-color: ${({ $isActive, theme }) => ($isActive ? theme.color.accent + '30' : 'transparent')};
    color: ${(props) => props.theme.color.text};
    transition: background-color 0.2s;
    cursor: pointer;
    border: none;
    text-align: left;

    &:hover {
        background-color: ${({ $isActive, theme }) =>
            $isActive ? theme.color.accent + '30' : theme.color.accent + '15'};
    }
`;

const CompletionLabel = styled.span`
    flex: 1;
    color: ${(props) => props.theme.color.text};
`;

const CompletionPercentage = styled.span<{ $isComplete: boolean }>`
    font-weight: 500;
    color: ${({ $isComplete, theme }) => ($isComplete ? theme.color.success : theme.color.textBody)};
`;

const MainContent = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 1rem 0;
    gap: 1rem;
`;

const ControlList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const ControlEditorBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-top: 0.5rem;
`;

const SharedSectionLabel = styled(TitleXSmall)`
    margin-top: 0.5rem;
`;

const SharedFieldsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.5rem 1rem;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    background-color: ${(props) => props.theme.color.cardHover};
    border: 1px dashed ${(props) => props.theme.color.border};
`;

const Description = styled.div`
    color: ${(props) => props.theme.color.textBody};
    margin-bottom: 0.5rem;
    padding: 1rem;
    background-color: ${(props) => props.theme.color.cardHover};
    border-radius: 0.375rem;
    border: 1px solid ${(props) => props.theme.color.border};
`;

const EmptyState = styled.div`
    padding: 2rem;
    text-align: center;
    color: ${(props) => props.theme.color.textBody};
    border-radius: 0.5rem;
    border: 1px dashed ${(props) => props.theme.color.border};
`;

const AddControlRow = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const ReadOnlyIdRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-radius: 0.375rem;
    background-color: ${(props) => props.theme.color.cardHover};
    border: 1px solid ${(props) => props.theme.color.border};
`;

const ReadOnlyIdLabel = styled.span`
    ${TextSize80}
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${(props) => props.theme.color.textBody};
`;

const ReadOnlyIdValue = styled.code`
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    color: ${(props) => props.theme.color.text};
    word-break: break-all;
`;

interface ProjectDeviceTypeCustomControlsProps {
    hideSaveButton?: boolean;
}

export default function ProjectDeviceTypeCustomControls({
    hideSaveButton = false,
}: ProjectDeviceTypeCustomControlsProps) {
    const { t } = useTranslation();
    const { organizationId } = useResolveCurrentOrg();
    const { deviceId } = useParams();
    const { showSuccess, showError } = useAlertsStore.getState();
    const loadingCustomControls = useEditDeviceTypeStore((state) => state.loadingCustomControls);
    const customControlsHasChanges = useEditDeviceTypeStore((state) => state.customControlsHasChanges);

    const {
        activeLocale,
        setActiveLocale,
        isSaving,
        LOCALES,
        currentControls,
        totalControlCount,
        getLocaleCompletion,
        isLocaleCustomized,
        isControlCompleteById,
        isControlNew,
        getControlRowKey,
        handlePerLocaleFieldChange,
        handleSharedFieldChange,
        handleIdChange,
        handleAddControl,
        saveCustomControls,
    } = useDeviceTypeCustomControlsEditor(organizationId, deviceId);

    const handleSave = async () => {
        await saveCustomControls(showSuccess, showError);
    };

    if (loadingCustomControls) {
        return <SpacedProgress />;
    }

    return (
        <>
            <SubSectionRow title={t('common:organization.projects.deviceType.customControls.title')} $sticky={true}>
                {!hideSaveButton && (
                    <Button onClick={handleSave} disabled={!customControlsHasChanges || isSaving}>
                        {isSaving ? t('common:saving') : t('common:save')}
                    </Button>
                )}
            </SubSectionRow>

            <ContentWrapper>
                <Sidebar>
                    <div>
                        <SectionLabel>
                            <Icons name="globe" ariaLabel={t('common:localization.titleShort')} />
                            {t('common:localization.titleShort')}
                        </SectionLabel>
                        <SplitButton
                            options={LOCALES.map((locale) => locale.code)}
                            selectedIndex={LOCALES.findIndex((l) => l.code === activeLocale)}
                            renderOptionLabel={(option) => {
                                const locale = LOCALES.find((l) => l.code === option);
                                return (
                                    <span className="flex items-center gap-2">
                                        <span>{locale?.flag}</span>
                                        <span>{locale?.label}</span>
                                    </span>
                                );
                            }}
                            groupLabel={t('common:localization.select')}
                            onSelect={(option) => setActiveLocale(option as VoiceLocales)}
                        />
                    </div>

                    <SectionLabel id="custom-controls-completion-label">
                        {t('common:organization.completionStatus.title')}
                    </SectionLabel>
                    <NavList aria-labelledby="custom-controls-completion-label">
                        {LOCALES.map((locale) => {
                            const isFallback = locale.code !== DefaultVoiceLocale && !isLocaleCustomized(locale.code);
                            const percentage = isFallback ? null : getLocaleCompletion(locale.code);
                            const isComplete = percentage === 100;
                            return (
                                <CompletionButton
                                    key={locale.code}
                                    $isActive={activeLocale === locale.code}
                                    onClick={() => setActiveLocale(locale.code)}>
                                    <span>
                                        {isFallback ? (
                                            <Icons
                                                name="globe"
                                                size={15}
                                                ariaLabel={t(
                                                    'common:organization.projects.deviceType.customControls.fallback'
                                                )}
                                            />
                                        ) : isComplete ? (
                                            <Icons
                                                name="check"
                                                size={15}
                                                ariaLabel={t('common:organization.completionStatus.complete')}
                                            />
                                        ) : (
                                            <Icons
                                                name="warningTriangle"
                                                size={15}
                                                ariaLabel={t('common:organization.completionStatus.incomplete')}
                                            />
                                        )}
                                    </span>
                                    <CompletionLabel>{locale.label}</CompletionLabel>
                                    <CompletionPercentage $isComplete={isComplete}>
                                        {isFallback
                                            ? t('common:organization.projects.deviceType.customControls.fallback')
                                            : `${percentage}%`}
                                    </CompletionPercentage>
                                </CompletionButton>
                            );
                        })}
                    </NavList>
                </Sidebar>

                <MainContent>
                    <Description>
                        <BodySmall>{t('common:organization.projects.deviceType.customControls.description')}</BodySmall>
                    </Description>

                    <AddControlRow>
                        <TextButton startIcon="plus" onClick={() => handleAddControl()}>
                            {t('common:organization.projects.deviceType.customControls.addControl')}
                        </TextButton>
                    </AddControlRow>

                    {totalControlCount === 0 ? (
                        <EmptyState>{t('common:organization.projects.deviceType.customControls.empty')}</EmptyState>
                    ) : (
                        <ControlList>
                            {currentControls.map((control) => (
                                <ControlEditor
                                    key={getControlRowKey(control.id)}
                                    control={control}
                                    isComplete={isControlCompleteById(control.id)}
                                    isNew={isControlNew(control.id)}
                                    onPerLocaleChange={handlePerLocaleFieldChange}
                                    onSharedChange={handleSharedFieldChange}
                                    onIdChange={handleIdChange}
                                />
                            ))}
                        </ControlList>
                    )}
                </MainContent>
            </ContentWrapper>
        </>
    );
}

interface ControlEditorProps {
    control: CustomControlModel;
    isComplete: boolean;
    isNew: boolean;
    onPerLocaleChange: (
        controlId: string,
        fieldName: 'name' | 'description' | 'defaultCommands',
        value: string | string[]
    ) => void;
    onSharedChange: <K extends SharedField>(controlId: string, fieldName: K, value: CustomControlModel[K]) => void;
    onIdChange: (oldId: string, newId: string) => void;
}

function ControlEditor({
    control,
    isComplete,
    isNew,
    onPerLocaleChange,
    onSharedChange,
    onIdChange,
}: ControlEditorProps) {
    const { t } = useTranslation();

    const commands = control.defaultCommands && control.defaultCommands.length > 0 ? control.defaultCommands : [''];

    const handleCommandChange = (index: number, value: string) => {
        const next = [...commands];
        next[index] = value;
        onPerLocaleChange(control.id, 'defaultCommands', next);
    };

    const handleCommandRemove = (index: number) => {
        const next = commands.filter((_, i) => i !== index);
        onPerLocaleChange(control.id, 'defaultCommands', next.length > 0 ? next : ['']);
    };

    const handleCommandAdd = () => {
        onPerLocaleChange(control.id, 'defaultCommands', [...commands, '']);
    };

    const title = control.name || t('common:organization.projects.deviceType.customControls.untitled');

    return (
        <AccordionRow
            title={title}
            description={control.description}
            startIcon={isComplete ? 'check' : 'warningTriangle'}>
            <ControlEditorBody>
                {isNew ? (
                    <TextField
                        label={t('common:organization.projects.deviceType.customControls.controlId')}
                        value={control.id}
                        onChange={(e) => onIdChange(control.id, e.target.value)}
                        helperText={t('common:organization.projects.deviceType.customControls.controlIdHelp')}
                        error={!control.id || !control.id.trim()}
                        fullWidth
                    />
                ) : (
                    <ReadOnlyIdRow>
                        <ReadOnlyIdLabel>
                            {t('common:organization.projects.deviceType.customControls.controlId')}
                        </ReadOnlyIdLabel>
                        <ReadOnlyIdValue>{control.id}</ReadOnlyIdValue>
                    </ReadOnlyIdRow>
                )}
                <TextField
                    label={t('common:organization.projects.deviceType.customControls.controlName')}
                    value={control.name || ''}
                    onChange={(e) => onPerLocaleChange(control.id, 'name', e.target.value)}
                    fullWidth
                />
                <TextField
                    label={t('common:organization.projects.deviceType.customControls.controlDescription')}
                    value={control.description || ''}
                    onChange={(e) => onPerLocaleChange(control.id, 'description', e.target.value)}
                    multiline
                    rows={2}
                    fullWidth
                />

                <SharedSectionLabel as="div">
                    {t('common:organization.projects.deviceType.customControls.defaultCommandsLabel')}
                </SharedSectionLabel>
                <LightXSmall>
                    {t('common:organization.projects.deviceType.customControls.defaultCommandsHelp')}
                </LightXSmall>
                {commands.map((command, index) => (
                    <CommandField
                        key={index}
                        startIcon="voice"
                        value={command}
                        commandType="voice"
                        onChange={(value) => handleCommandChange(index, value)}
                        onRemove={() => handleCommandRemove(index)}
                    />
                ))}
                <ChipButtonContainer>
                    <TextButton startIcon="plus" onClick={handleCommandAdd}>
                        {t('common:input.addMore')}
                    </TextButton>
                </ChipButtonContainer>

                <SharedSectionLabel as="div">
                    {t('common:organization.projects.deviceType.customControls.sharedAcrossLanguages')}
                </SharedSectionLabel>
                <LightXSmall>
                    {t('common:organization.projects.deviceType.customControls.sharedAcrossLanguagesHelp')}
                </LightXSmall>
                <SharedFieldsGrid>
                    <TextField
                        label={t('common:organization.projects.deviceType.customControls.iconUrl')}
                        value={control.iconUrl || ''}
                        onChange={(e) => onSharedChange(control.id, 'iconUrl', e.target.value)}
                        fullWidth
                    />
                    <TextField
                        label={t('common:organization.projects.deviceType.customControls.contextGroupName')}
                        value={control.contextGroupName || ''}
                        onChange={(e) => onSharedChange(control.id, 'contextGroupName', e.target.value)}
                        fullWidth
                    />
                    <BooleanToggle
                        checked={!!control.isProfileControl}
                        label={t('common:organization.projects.deviceType.customControls.isProfileControl')}
                        onChange={(value) => onSharedChange(control.id, 'isProfileControl', value)}
                    />
                    <BooleanToggle
                        checked={!!control.isPrefixVoiceCommand}
                        label={t('common:organization.projects.deviceType.customControls.isPrefixVoiceCommand')}
                        onChange={(value) => onSharedChange(control.id, 'isPrefixVoiceCommand', value)}
                    />
                    <BooleanToggle
                        checked={!!control.allowWhenDictating}
                        label={t('common:organization.projects.deviceType.customControls.allowWhenDictating')}
                        onChange={(value) => onSharedChange(control.id, 'allowWhenDictating', value)}
                    />
                    <BooleanToggle
                        checked={!!control.allowsHold}
                        label={t('common:organization.projects.deviceType.customControls.allowsHold')}
                        onChange={(value) => onSharedChange(control.id, 'allowsHold', value)}
                    />
                    <BooleanToggle
                        checked={!!control.isDeprecated}
                        label={t('common:organization.projects.deviceType.customControls.isDeprecated')}
                        onChange={(value) => onSharedChange(control.id, 'isDeprecated', value)}
                    />
                    <BooleanToggle
                        checked={!!control.requiresEmployeeAccess}
                        label={t('common:organization.projects.deviceType.customControls.requiresEmployeeAccess')}
                        onChange={(value) => onSharedChange(control.id, 'requiresEmployeeAccess', value)}
                    />
                    <BooleanToggle
                        checked={!!control.requiresStudentAccess}
                        label={t('common:organization.projects.deviceType.customControls.requiresStudentAccess')}
                        onChange={(value) => onSharedChange(control.id, 'requiresStudentAccess', value)}
                    />
                    <BooleanToggle
                        checked={!!control.requiresGenericLicense}
                        label={t('common:organization.projects.deviceType.customControls.requiresGenericLicense')}
                        onChange={(value) => onSharedChange(control.id, 'requiresGenericLicense', value)}
                    />
                    <BooleanToggle
                        checked={!!control.requiresPlusAccess}
                        label={t('common:organization.projects.deviceType.customControls.requiresPlusAccess')}
                        onChange={(value) => onSharedChange(control.id, 'requiresPlusAccess', value)}
                    />
                </SharedFieldsGrid>
            </ControlEditorBody>
        </AccordionRow>
    );
}

interface BooleanToggleProps {
    checked: boolean;
    label: string;
    onChange: (value: boolean) => void;
}

function BooleanToggle({ checked, label, onChange }: BooleanToggleProps) {
    return (
        <FormControlLabel
            control={<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />}
            label={<TitleXSmall as="span">{label}</TitleXSmall>}
            labelPlacement="end"
        />
    );
}
